import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
type FAQItem = {
  question: string;
  answer: string;
};
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs: FAQItem[] = [{
    question: '¿Qué aprenderé exactamente en esta guía?',
    answer: 'Aprenderás desde la selección de la resina adecuada, proporciones de mezcla, técnicas de pigmentación, eliminación de burbujas y el proceso de lijado y pulido para un acabado espejo.'
  }, {
    question: '¿Necesito un taller profesional para empezar?',
    answer: 'No. La guía está diseñada para que puedas empezar en un espacio pequeño de tu casa, utilizando herramientas básicas y enfocándote en la técnica de la resina.'
  }, {
    question: '¿Sirve para hacer mesas de río (River Tables)?',
    answer: 'Sí, incluimos un módulo especial sobre el tratamiento de la madera y cómo realizar coladas de gran espesor para mesas de río exitosas.'
  }, {
    question: '¿Cómo recibo mi material tras la compra?',
    answer: 'El envío es automático e inmediato. Una vez confirmado el pago, recibirás un enlace de descarga en tu correo electrónico en formato PDF.'
  }, {
    question: '¿Puedo leer la guía desde mi celular?',
    answer: 'Totalmente. Los archivos están optimizados para visualizarse correctamente en smartphones, tablets y computadoras.'
  }, {
    question: '¿Qué pasa si tengo una duda técnica?',
    answer: 'Contamos con un canal de soporte vía email donde respondemos consultas puntuales sobre los procesos explicados en la guía.'
  }, {
    question: '¿Los materiales son fáciles de conseguir?',
    answer: 'Sí, te orientamos sobre qué buscar y cómo pedir los materiales en tiendas de químicos, pinturas o ferreterías industriales.'
  }, {
    question: '¿Es apto para alguien que nunca usó resina?',
    answer: 'Definitivamente. Empezamos desde lo más básico para que no cometas los errores típicos de principiante que hacen perder mucho dinero.'
  }, {
    question: '¿Ofrecen garantía de satisfacción?',
    answer: 'Sí, tenés 7 días de garantía. Si el contenido no es lo que esperabas o el archivo tiene fallas, te devolvemos tu dinero.'
  }, {
    question: '¿Incluye proveedores de resina?',
    answer: 'Te damos las pautas para identificar proveedores de calidad en tu zona y qué especificaciones técnicas debe tener la resina según el proyecto.'
  }, {
    question: '¿Es una estafa? ¿Cómo sé que recibiré el material?',
    answer: 'No es una estafa; somos una empresa real con miles de alumnos. El proceso de pago es gestionado por plataformas seguras como Mercado Pago, y el envío del material es automático e inmediato a tu email tras la compra.'
  }];
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="py-12 px-4 bg-[#fdf8f3] relative z-10 border-t border-gray-200" data-id="element-137" data-mp="1f5u8mkqvtckv">
      <div className="max-w-3xl mx-auto" data-id="element-138" data-mp="1b3z8hjkot58o">
        <div className="text-center mb-8" data-id="element-139" data-mp="1b50irltvpnpc">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-gray-300 text-gray-600 text-sm font-medium mb-3" data-id="element-140" data-mp="1b5tvd3fir7fe">
            <span className="flex h-2 w-2 rounded-full bg-[#d97706]" data-id="element-141" data-mp="1p4086vp7u6mk"></span>
            ¿Tenés dudas?
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-2 tracking-tight" data-id="element-142" data-mp="2mje86v4a6b7h">
            Preguntas Frecuentes
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-gray-600" data-id="element-143" data-mp="1sdt4gtyvp1ty">
            Todo lo que necesitás saber antes de empezar
          </motion.p>
        </div>

        <div className="space-y-4" data-id="element-144" data-mp="2g9dy4myeurty">
          {faqs.map((faq, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.05
        }} className="bg-white border border-gray-300 rounded-2xl overflow-hidden hover:border-gray-400 transition-colors shadow-sm" data-id="element-145" data-mp="3401job191fyz" data-mpi={index}>
              <button onClick={() => toggleFAQ(index)} aria-expanded={openIndex === index} className="w-full flex items-center justify-between p-4 text-left focus:outline-none group" data-id="element-146" data-mp="3mxbl1jkrqsp3">
                <span className="text-lg font-bold text-gray-900 pr-8 group-hover:text-blue-600 transition-colors" data-id="element-147" data-mp="394lvj56175n8">
                  {faq.question}
                </span>
                <motion.div animate={{
              rotate: openIndex === index ? 180 : 0
            }} transition={{
              duration: 0.3
            }} className="shrink-0" data-id="element-148" data-mp="347loos1cxyv2">
                  <ChevronDownIcon className="w-5 h-5 text-gray-500 group-hover:text-blue-600 transition-colors" data-id="element-149" data-mp="rfg271kyloyu" />
                </motion.div>
              </button>

              <AnimatePresence data-id="element-150" data-mp="10ge9ptulwem6">
                {openIndex === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="overflow-hidden" data-id="element-151" data-mp="15fqyik51fvre">
                    <div className="px-4 pb-4 text-gray-700 leading-relaxed border-t border-gray-200 pt-3" data-id="element-152" data-mp="3l6dc7nsvtvhe">
                      {faq.answer}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>

        {/* Support CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3
      }} className="mt-12 p-6 bg-white border border-gray-300 rounded-2xl text-center shadow-sm" data-id="element-153" data-mp="3dljc6g16mxzr">
          <p className="text-gray-600 mb-3" data-id="element-154" data-mp="2pxt047jgtsbk">
            ¿Todavía tenés dudas o problemas con tu compra?
          </p>
          <a href="mailto:soportesinfinstudio@gmail.com" className="inline-block bg-gray-900 text-white font-bold px-6 py-3 rounded-xl hover:bg-gray-800 transition-colors" data-id="element-155" data-mp="2kz8bz6hhmz3h">
            Contactanos: soportedigitalfactory@gmail.com
          </a>
        </motion.div>
      </div>
    </section>;
}